import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import GaugeChart from "react-gauge-chart";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";
import { getIndividualForMyTeam } from "services/api";
import { getdashboardData } from "services/api";
import PropTypes from "prop-types";
import { getImageSrc } from "services/utils";
import { getMyTeam } from "services/api";
import { useTranslation } from "react-i18next";

const getResultColor = (finalResult, ach) => {
  if (ach == null) return "grey";
  if (finalResult > 105) return "blue";
  if (finalResult >= 90 && finalResult <= 105) return "green";
  if (finalResult >= 80 && finalResult < 90) return "yellow";
  if (finalResult >= 70 && finalResult < 80) return "orange";
  return "red";
};

const getTrendIcon = (trend) => {
  if (trend > 0)
    return <TrendingUp fontSize="small" sx={{ color: "green", verticalAlign: "middle" }} />;
  if (trend < 0)
    return <TrendingDown fontSize="small" sx={{ color: "red", verticalAlign: "middle" }} />;
  return <TrendingFlat fontSize="small" sx={{ color: "grey", verticalAlign: "middle" }} />;
};

// Replace with your real API functions

const PerspectiveView = ({ individual, team, month, year, employeeId }) => {
  const { t } = useTranslation();
  const [individualData, setIndividualData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (!individual || individual.length < 1 || !team || team.length < 1) {
        const [individual, team, dashboard] = await Promise.all([
          getIndividualForMyTeam(month, year, employeeId),
          getMyTeam(month, year, employeeId),
          getdashboardData(month, year, employeeId),
        ]);
        console.log("Dashboard Data (Initial):", dashboard);
        if (individual != "error") setIndividualData(individual);
        if (team != "error") setTeamData(team);
        if (dashboard != "error") setDashboardData(dashboard);
      } else {
        const dashboard = await getdashboardData(month, year, employeeId);
        console.log("Dashboard Data (Initial):", dashboard);

        setDashboardData(dashboard);
        setTeamData(team);
        setIndividualData(individual);
      }
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, [month, year]);

  const myData = individualData[0];
  const myDashboard = myData ? dashboardData[myData[1]] : [];

  return (
    <>
      <Typography variant="h4" gutterBottom>
        {t("user.myTeam.statisticsOverview")}
      </Typography>

      {/* My Data */}
      {myData && (
        <Card variant="outlined" sx={{ mb: 4, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t("user.myTeam.myPerformance")}
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <IndividualStatTable data={myData} dashboard={myDashboard} />
          )}
        </Card>
      )}

      {/* Team Table */}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t("user.myTeam.teamPerformance")}
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={1}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <TeamStatTable
            data={teamData}
            dashboardData={dashboardData}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        )}
      </Card>
    </>
  );
};

const IndividualStatTable = ({ data, dashboard }) => {
  const { t } = useTranslation();

  const [employeeId, name, jobId, averageRaw, trend, photo, jobName] = data;
  const average = parseFloat(averageRaw);
  const allPerspectives = dashboard?.map((p) => p.perspectiveName);

  const perspectiveMap = {};
  const weightPerspectiveMap = {};
  const trendPerspectiveMap = {};
  dashboard?.forEach((p) => {
    perspectiveMap[p.perspectiveName] = p.averageScore.toFixed(2);
    weightPerspectiveMap[p.perspectiveName] = p.weight;
    trendPerspectiveMap[p.perspectiveName] = p.trend;
  });

  return (
    <TableContainer
      component={Paper}
      sx={{ overflowX: "auto", boxShadow: "none", border: "1px solid #e0e0e0" }}
    >
      <Table
        size="small"
        sx={{
          tableLayout: "auto",
          minWidth: "max-content",
          "& .MuiTableCell-root": {
            py: 1.5,
            px: 1.5,
            borderRight: "1px solid #f0f0f0",
            "&:last-child": {
              borderRight: "none",
            },
          },
        }}
      >
        <TableHead sx={{ bgcolor: "#f7f7f7" }}>
          <TableRow>
            {allPerspectives?.map((p) => (
              <TableCell
                key={p}
                align="center"
                sx={{
                  minWidth: 120,
                  fontWeight: 600,
                  px: 2,
                }}
              ></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            sx={{
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            <TableCell align="center" sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -35,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {t("user.myTeam.photo")}
              </Box>
              <Avatar
                src={photo ? getImageSrc(photo) : undefined}
                sx={{
                  width: 90,
                  height: 90,
                  mx: "auto",
                  border: "1px solid #e0e0e0",
                }}
              />
            </TableCell>

            <TableCell sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {t("user.myTeam.employee")}
              </Box>
              <Box display="flex" alignItems="center">
                <Box ml={1}>
                  <Typography fontWeight={600} noWrap>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {jobName}
                  </Typography>
                </Box>
              </Box>
            </TableCell>

            <TableCell align="center" sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {t("user.myTeam.averageScore")}
              </Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 90 }}>
                  <GaugeChart
                    id={`gauge-${employeeId}`}
                    nrOfLevels={30}
                    percent={isNaN(average) ? 0 : average / 100}
                    hideText
                    colors={["#EA4228", "#F5CD19", "#5BE12C"]}
                    arcWidth={0.3}
                    needleColor="#444"
                    needleBaseColor="#222"
                  />
                </Box>
                <Typography variant="body2" sx={{ mt: -1, fontWeight: 500 }}>
                  {isNaN(average) ? t(`user.myTeam.noData`) : `${average?.toFixed(2)}%`}
                </Typography>
              </Box>
            </TableCell>

            {allPerspectives?.map((p) => (
              <TableCell
                key={p}
                align="center"
                sx={{
                  position: "relative",
                  fontWeight: 500,
                  color: perspectiveMap[p] ? "inherit" : "text.secondary",
                  minWidth: 120,
                }}
              >
                <Tooltip title={t(`user.assess.perspectives.${p}`)} placement="top">
                  <Box
                    sx={{
                      position: "absolute",
                      top: -40,
                      left: 0,
                      right: 0,
                      textAlign: "center",
                      fontWeight: 600,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.2,
                      px: 0.5,
                      maxHeight: 40,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {t(`user.assess.perspectives.${p}`)}
                  </Box>
                </Tooltip>
                {t("user.myTeam.weight")}:{" "}
                {weightPerspectiveMap[p] ? `${weightPerspectiveMap[p]}%` : "--"}
                <br />
                {t("user.myTeam.achievement")}:{" "}
                {perspectiveMap[p] ? (
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    {/* Colored circle */}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: () => {
                          const finalResult =
                            perspectiveMap[p] && weightPerspectiveMap[p]
                              ? (perspectiveMap[p] / weightPerspectiveMap[p]) * 100
                              : null;
                          return getResultColor(finalResult, finalResult);
                        },
                        display: "inline-block",
                      }}
                    />
                    {`${perspectiveMap[p]}%`}
                  </Box>
                ) : (
                  "--"
                )}
                <br />
                {/* Trend icon */}
                <Box mt={0.5}>
                  {trendPerspectiveMap[p] != null && getTrendIcon(trendPerspectiveMap[p])}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Utility to extract all unique perspectives
const getUniquePerspectives = (dashboardData) => {
  const perspectiveSet = new Set();
  Object.values(dashboardData)?.forEach((perspectives) => {
    perspectives?.forEach((p) => perspectiveSet.add(p.perspectiveName));
  });
  return Array.from(perspectiveSet);
};

const TeamStatTable = ({ data, dashboardData }) => {
  const { t } = useTranslation();

  const allPerspectives = getUniquePerspectives(dashboardData);

  return (
    <TableContainer
      component={Paper}
      sx={{ overflowX: "auto", boxShadow: "none", border: "1px solid #e0e0e0" }}
    >
      <Table
        sx={{
          tableLayout: "auto",
          "& .MuiTableCell-root": {
            py: 1.5,
            px: 1.5,
            borderRight: "1px solid #f0f0f0",
            "&:last-child": {
              borderRight: "none",
            },
          },
        }}
      >
        <TableHead sx={{ bgcolor: "#f7f7f7" }}>
          <TableRow>
            {/* Empty cells for the header row except first column */}
            {allPerspectives?.map((p) => (
              <TableCell
                key={p}
                align="center"
                sx={{
                  minWidth: 50,
                  fontWeight: 600,
                  px: 2,
                }}
              ></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((emp, index) => {
            const [id, name, jobId, averageRaw, trend, photo, jobName] = emp;
            const average = parseFloat(averageRaw);
            const perspectives = dashboardData[name] || [];

            const perspectiveMap = {};
            const weightPerspectiveMap = {};
            const trendPerspectiveMap = {};
            perspectives?.forEach((p) => {
              perspectiveMap[p.perspectiveName] = p.averageScore.toFixed(2);
              weightPerspectiveMap[p.perspectiveName] = p.weight;
              trendPerspectiveMap[p.perspectiveName] = p.trend;
            });

            return (
              <TableRow
                key={id}
                sx={{
                  "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                {/* Only the first row gets the headers above cells */}
                {index === 0 ? (
                  <>
                    <TableCell align="center" sx={{ position: "relative", width: 100 }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: -35,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        {t("user.myTeam.photo")}
                      </Box>
                      <Avatar
                        src={photo ? getImageSrc(photo) : undefined}
                        sx={{
                          width: 90,
                          height: 90,
                          mx: "auto",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ position: "relative", width: 200 }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: -40,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        {t("user.myTeam.employee")}
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Box ml={1} maxWidth={300}>
                          <Tooltip title={name}>
                            <Typography
                              fontWeight={600}
                              noWrap
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {name}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={jobName}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {jobName}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ position: "relative" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: -40,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        {t("user.myTeam.averageScore")}
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ width: 90 }}>
                          <GaugeChart
                            id={`gauge-${id}`}
                            nrOfLevels={30}
                            percent={isNaN(average) ? 0 : average / 100}
                            hideText
                            colors={["#EA4228", "#F5CD19", "#5BE12C"]}
                            arcWidth={0.3}
                            needleColor="#444"
                            needleBaseColor="#222"
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mt: -1, fontWeight: 500 }}>
                          {isNaN(average) ? t(`user.myTeam.noData`) : `${average.toFixed(2)}%`}
                        </Typography>
                      </Box>
                    </TableCell>
                    {allPerspectives?.map((p, i) => (
                      <TableCell
                        key={p}
                        align="center"
                        sx={{
                          position: "relative",
                          fontWeight: 500,
                          color: perspectiveMap[p] ? "inherit" : "text.secondary",
                          minWidth: 120,
                        }}
                      >
                        <Tooltip title={t(`user.assess.perspectives.${p}`)} placement="top">
                          <Box
                            sx={{
                              position: "absolute",
                              top: -40,
                              left: 0,
                              right: 0,
                              textAlign: "center",
                              fontWeight: 600,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.2,
                              px: 0.5,
                              maxHeight: 40,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {t(`user.assess.perspectives.${p}`)}
                          </Box>
                        </Tooltip>
                        {t("user.myTeam.weight")}:{" "}
                        {weightPerspectiveMap[p] ? `${weightPerspectiveMap[p]}%` : "--"}
                        <br />
                        {t("user.myTeam.achievement")}:{" "}
                        {perspectiveMap[p] ? (
                          <Box
                            component="span"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                          >
                            {/* Colored circle */}
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: () => {
                                  const finalResult =
                                    perspectiveMap[p] && weightPerspectiveMap[p]
                                      ? (perspectiveMap[p] / weightPerspectiveMap[p]) * 100
                                      : null;
                                  return getResultColor(finalResult, finalResult);
                                },
                                display: "inline-block",
                              }}
                            />
                            {`${perspectiveMap[p]}%`}
                          </Box>
                        ) : (
                          "--"
                        )}
                        <br />
                        {/* Trend icon */}
                        <Box mt={0.5}>
                          {trendPerspectiveMap[p] != null && getTrendIcon(trendPerspectiveMap[p])}
                        </Box>
                      </TableCell>
                    ))}
                  </>
                ) : (
                  <>
                    <TableCell align="center">
                      <Avatar
                        src={photo ? getImageSrc(photo) : undefined}
                        sx={{
                          width: 90,
                          height: 90,
                          mx: "auto",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box ml={1} maxWidth={300}>
                          <Tooltip title={name}>
                            <Typography
                              fontWeight={600}
                              noWrap
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {name}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={jobName}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {jobName}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ width: 90 }}>
                          <GaugeChart
                            id={`gauge-${id}`}
                            nrOfLevels={30}
                            percent={isNaN(average) ? 0 : average / 100}
                            hideText
                            colors={["#EA4228", "#F5CD19", "#5BE12C"]}
                            arcWidth={0.3}
                            needleColor="#444"
                            needleBaseColor="#222"
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mt: -1, fontWeight: 500 }}>
                          {isNaN(average) ? t(`user.myTeam.noData`) : `${average.toFixed(2)}%`}
                        </Typography>
                      </Box>
                    </TableCell>
                    {allPerspectives?.map((p) => (
                      <TableCell
                        key={p}
                        align="center"
                        sx={{
                          fontWeight: 500,
                          color: perspectiveMap[p] ? "inherit" : "text.secondary",
                        }}
                      >
                        {t("user.myTeam.weight")}:{" "}
                        {weightPerspectiveMap[p] ? `${weightPerspectiveMap[p]}%` : "--"}
                        <br />
                        {t("user.myTeam.achievement")}:{" "}
                        {perspectiveMap[p] ? (
                          <Box
                            component="span"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                          >
                            {/* Colored circle */}
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: () => {
                                  const finalResult =
                                    perspectiveMap[p] && weightPerspectiveMap[p]
                                      ? (perspectiveMap[p] / weightPerspectiveMap[p]) * 100
                                      : null;
                                  return getResultColor(finalResult, finalResult);
                                },
                                display: "inline-block",
                              }}
                            />
                            {`${perspectiveMap[p]}%`}
                          </Box>
                        ) : (
                          "--"
                        )}
                        <br />
                        {/* Trend icon */}
                        <Box mt={0.5}>
                          {trendPerspectiveMap[p] != null && getTrendIcon(trendPerspectiveMap[p])}
                        </Box>
                      </TableCell>
                    ))}
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

IndividualStatTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.number, // employeeId
      PropTypes.string, // name, photo, job name
      PropTypes.number, // jobId, average, trend
    ])
  ).isRequired,
  dashboard: PropTypes.arrayOf(
    PropTypes.shape({
      perspectiveId: PropTypes.number.isRequired,
      perspectiveName: PropTypes.string.isRequired,
      averageScore: PropTypes.number.isRequired,
      weight: PropTypes.number.isRequired,
    })
  ).isRequired,
};

TeamStatTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.any]))
  ).isRequired,
  dashboardData: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        perspectiveId: PropTypes.number.isRequired,
        perspectiveName: PropTypes.string.isRequired,
        averageScore: PropTypes.number.isRequired,
        weight: PropTypes.number.isRequired,
      })
    )
  ).isRequired,
};

PerspectiveView.propTypes = {
  individual: PropTypes.array.isRequired,
  team: PropTypes.array.isRequired,
  month: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
  employeeId: PropTypes.string.isRequired,
};

export default PerspectiveView;
